import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, updateDoc, increment, collection, addDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './useAuth';

export function useSlices() {
  const { user } = useAuth();
  const [sliceBalance, setSliceBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSliceBalance = useCallback(async () => {
    if (!user) return;
    console.log('loadSliceBalance - User ID:', user.uid);

    try {
      setLoading(true);
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const currentBalance = userDoc.data().slices || 0;
        console.log('loadSliceBalance - User:', user.uid, 'Saldo:', currentBalance);
        setSliceBalance(currentBalance);
      }
      setError(null);
    } catch (err) {
      console.error('Errore nel caricamento degli spicchi:', err);
      setError('Errore nel caricamento degli spicchi');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const useSliceForPizza = async () => {
    if (!user) throw new Error('Utente non autenticato');
    console.log('useSliceForPizza - User ID:', user.uid);

    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) throw new Error('Utente non trovato');
      
      const currentSlices = userDoc.data().slices || 0;
      if (currentSlices < 1) return false;

      await updateDoc(userRef, {
        slices: increment(-1)
      });

      await addDoc(collection(db, 'sliceTransactions'), {
        userId: user.uid,
        type: 'use',
        amount: -1,
        createdAt: new Date().toISOString()
      });

      return true;
    } catch (err) {
      console.error('Errore nell\'uso dello spicchio:', err);
      return false;
    }
  };

  const refundSlice = async () => {
    if (!user) throw new Error('Utente non autenticato');
    console.log('refundSlice - User ID:', user.uid);

    try {
      const userRef = doc(db, 'users', user.uid);
      
      // Log prima dell'operazione
      const beforeDoc = await getDoc(userRef);
      const beforeSlices = beforeDoc.exists() ? beforeDoc.data().slices : 0;
      console.log('Prima del refund - User:', user.uid, 'Saldo:', beforeSlices);

      // Usiamo una transazione per garantire l'atomicità
      await updateDoc(userRef, {
        slices: increment(1)
      });

      // Log dopo l'operazione
      const afterDoc = await getDoc(userRef);
      const afterSlices = afterDoc.exists() ? afterDoc.data().slices : 0;
      console.log('Dopo il refund - User:', user.uid, 'Saldo:', afterSlices);

      // Registriamo la transazione
      await addDoc(collection(db, 'sliceTransactions'), {
        userId: user.uid,
        type: 'refund',
        amount: 1,
        createdAt: new Date().toISOString(),
        previousBalance: beforeSlices,
        newBalance: afterSlices
      });

      return true;
    } catch (err) {
      console.error('Errore nel rimborso dello spicchio:', err);
      return false;
    }
  };

  const purchaseSlices = async (amount: number) => {
    if (!user) throw new Error('Utente non autenticato');
    console.log('purchaseSlices - User ID:', user.uid);

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        slices: increment(amount)
      });

      await addDoc(collection(db, 'sliceTransactions'), {
        userId: user.uid,
        type: 'purchase',
        amount,
        createdAt: new Date().toISOString()
      });

      return true;
    } catch (err) {
      console.error('Errore nell\'acquisto degli spicchi:', err);
      return false;
    }
  };

  // Carica il saldo solo quando cambia l'utente
  useEffect(() => {
    if (user) {
      console.log('Initial load - User ID:', user.uid);
      loadSliceBalance();
    }
  }, [user, loadSliceBalance]);

  return {
    sliceBalance,
    loading,
    error,
    useSliceForPizza,
    refundSlice,
    purchaseSlices,
    reloadBalance: loadSliceBalance
  };
}